import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  CurrencyDollar,
  MagnifyingGlass,
  TrendUp,
  CreditCard,
  Receipt,
  Warning,
  CheckCircle,
  Download,
  Eye,
  Buildings,
  XCircle,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

export default function BillingPage() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPlan, setFilterPlan] = useState('all')
  const [selectedPeriod, setSelectedPeriod] = useState('current')

  // TODO: Replace with actual API data
  const metrics = {
    monthlyRevenue: 145890,
    annualRevenue: 1658200,
    pendingPayments: 12450,
    activeSubscriptions: 22,
  }

  // TODO: Replace with actual API data from Supabase/payment gateway
  const subscriptions = [
    {
      id: 's1',
      tenant_id: 't1',
      tenant_name: 'Acme Logistics Corp',
      plan: 'Enterprise',
      price: 2499,
      status: 'active',
      next_billing: '2024-04-15',
      payment_method: 'Cartão •••• 4242',
      mrr: 2499,
      created_at: '2024-01-15',
      last_payment: '2024-03-15',
    },
    {
      id: 's2',
      tenant_id: 't2',
      tenant_name: 'Global Freight Solutions',
      plan: 'Professional',
      price: 999,
      status: 'active',
      next_billing: '2024-04-03',
      payment_method: 'Cartão •••• 1234',
      mrr: 999,
      created_at: '2024-02-03',
      last_payment: '2024-03-03',
    },
    {
      id: 's3',
      tenant_id: 't3',
      tenant_name: 'Express Shipping Ltd',
      plan: 'Basic',
      price: 299,
      status: 'overdue',
      next_billing: '2024-03-18',
      payment_method: 'Cartão •••• 5678',
      mrr: 299,
      created_at: '2024-02-18',
      last_payment: '2024-02-18',
    },
    {
      id: 's4',
      tenant_id: 't4',
      tenant_name: 'Quick Ship Ltda',
      plan: 'Professional',
      price: 999,
      status: 'cancelled',
      next_billing: null,
      payment_method: 'Cartão •••• 9999',
      mrr: 0,
      created_at: '2024-01-10',
      last_payment: '2024-02-10',
    },
    {
      id: 's5',
      tenant_id: 't5',
      tenant_name: 'Maritime Transport Inc',
      plan: 'Enterprise',
      price: 2499,
      status: 'pending',
      next_billing: '2024-04-01',
      payment_method: 'Cartão •••• 7777',
      mrr: 2499,
      created_at: '2024-03-01',
      last_payment: null,
    },
  ]

  // TODO: Replace with actual invoice data
  const recentInvoices = [
    {
      id: 'inv-001',
      tenant_name: 'Acme Logistics Corp',
      amount: 2499,
      status: 'paid',
      date: '2024-03-15',
      invoice_url: '#',
    },
    {
      id: 'inv-002',
      tenant_name: 'Global Freight Solutions',
      amount: 999,
      status: 'paid',
      date: '2024-03-03',
      invoice_url: '#',
    },
    {
      id: 'inv-003',
      tenant_name: 'Express Shipping Ltd',
      amount: 299,
      status: 'overdue',
      date: '2024-02-18',
      invoice_url: '#',
    },
    {
      id: 'inv-004',
      tenant_name: 'Maritime Transport Inc',
      amount: 2499,
      status: 'pending',
      date: '2024-03-01',
      invoice_url: '#',
    },
  ]

  // TODO: Implement search/filter functionality
  const filteredSubscriptions = subscriptions.filter((sub) => {
    const matchesSearch = sub.tenant_name
      .toLowerCase()
      .includes(searchQuery.toLowerCase())

    const matchesStatus = filterStatus === 'all' || sub.status === filterStatus
    const matchesPlan = filterPlan === 'all' || sub.plan === filterPlan

    return matchesSearch && matchesStatus && matchesPlan
  })

  const getStatusBadge = (status: string) => {
    const configs = {
      active: {
        className: 'bg-emerald-100 text-emerald-700',
        label: 'Ativo',
        icon: CheckCircle,
      },
      overdue: {
        className: 'bg-red-100 text-red-700',
        label: 'Vencido',
        icon: Warning,
      },
      pending: {
        className: 'bg-amber-100 text-amber-700',
        label: 'Pendente',
        icon: Warning,
      },
      cancelled: {
        className: 'bg-gray-100 text-gray-700',
        label: 'Cancelado',
        icon: XCircle,
      },
    }

    const config = configs[status as keyof typeof configs] || configs.pending
    const Icon = config.icon

    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}
      >
        {Icon && <Icon size={12} weight="fill" />}
        {config.label}
      </span>
    )
  }

  const getPlanBadge = (plan: string) => {
    const badges = {
      Enterprise: 'bg-violet-100 text-violet-700',
      Professional: 'bg-blue-100 text-blue-700',
      Basic: 'bg-gray-100 text-gray-700',
    }
    return badges[plan as keyof typeof badges] || 'bg-gray-100 text-gray-700'
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const getInitials = (name: string) => {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Faturamento e Assinaturas
              </h1>
            </div>
            <Button
              onClick={() => navigate('/master/billing/export')}
              size="sm"
              className="gap-2 h-9 bg-emerald-600 hover:bg-emerald-700"
            >
              <Download size={16} />
              Exportar
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Metrics Grid - 3 Column */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Monthly Revenue */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Receita Mensal (MRR)
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.monthlyRevenue)}</p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium text-emerald-600">+12%</span> vs mês passado
                </p>
              </div>
              <CurrencyDollar size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Annual Revenue */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Receita Anual (ARR)
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.annualRevenue)}</p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium text-emerald-600">+18%</span> vs ano passado
                </p>
              </div>
              <TrendUp size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Pending & Active */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Status
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Assinaturas Ativas</span>
                <span className="font-semibold text-gray-900">{metrics.activeSubscriptions}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Pendentes</span>
                <span className="font-semibold text-red-600">{formatCurrency(metrics.pendingPayments)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar - Horizontal Compact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                placeholder="Buscar tenant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 border-gray-200"
              />
            </div>

            {/* Period Selector */}
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="current">Período Atual</option>
              <option value="last_month">Mês Passado</option>
              <option value="last_quarter">Último Trimestre</option>
              <option value="last_year">Último Ano</option>
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todos Status</option>
              <option value="active">Ativo</option>
              <option value="pending">Pendente</option>
              <option value="overdue">Vencido</option>
              <option value="cancelled">Cancelado</option>
            </select>

            {/* Plan Filter */}
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todos Planos</option>
              <option value="Enterprise">Enterprise</option>
              <option value="Professional">Professional</option>
              <option value="Basic">Basic</option>
            </select>
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plano
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Próximo Pagamento
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Método
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSubscriptions.map((sub) => (
                  <tr
                    key={sub.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-emerald-700">
                            {getInitials(sub.tenant_name)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">{sub.tenant_name}</p>
                          <p className="text-xs text-gray-500 truncate">ID: {sub.tenant_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadge(sub.plan)}`}>
                        {sub.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(sub.status)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(sub.price)}
                      </span>
                      <span className="text-xs text-gray-500">/mês</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900">
                        {formatDate(sub.next_billing)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <CreditCard size={16} className="text-gray-400" />
                        {sub.payment_method}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/master/billing/subscription/${sub.id}`)}
                          className="h-8 w-8 p-0"
                        >
                          <Eye size={16} className="text-gray-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredSubscriptions.length === 0 && (
            <div className="text-center py-12 bg-gray-50">
              <Receipt size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
              <p className="text-gray-500 text-sm">Nenhuma assinatura encontrada</p>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{filteredSubscriptions.length}</span> assinaturas
            </div>
          </div>
        </div>

        {/* Recent Invoices */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
            Faturas Recentes
          </h3>
          <div className="space-y-3">
            {recentInvoices.map((invoice) => (
              <div
                key={invoice.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Receipt size={20} className="text-emerald-700" weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {invoice.tenant_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(invoice.date)} · {invoice.id}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {getStatusBadge(invoice.status)}
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(invoice.amount)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(invoice.invoice_url, '_blank')}
                    className="h-8 w-8 p-0"
                  >
                    <Download size={16} className="text-gray-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </AppLayout>
  )
}
